import React, { useState, useRef, useEffect } from 'react';
import { IndianRupee, ShoppingCart, Briefcase, FileText, Printer, X } from 'lucide-react';
import { Modal } from '../../../components/ui';
import { useAuthStore } from "@/store/authStore";
import { getCompanySettings } from "../../verve-settings/services/settingsService";
import { API_BASE_URL } from "@/services/api";

const formatLogoUrl = (path) => {
  if (!path || path === "undefined" || path === "null") return "";
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  let cleanPath = path.replace(/\\/g, "/");
  cleanPath = cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
  return `${API_BASE_URL}${cleanPath}`;
};

export const PaymentTransactionModal = ({ isOpen, onClose, account }) => {
  const [activeTab, setActiveTab] = useState('received'); // received, material, project
  const [fetchedSettings, setFetchedSettings] = useState(null);
  const printRef = useRef();
  const { companySettings } = useAuthStore();

  useEffect(() => {
    getCompanySettings()
      .then((res) => {
        const payload = res?.data?.data || res?.data || {};
        setFetchedSettings(payload);
      })
      .catch((err) => {
        console.warn("Failed to fetch company settings", err);
      });
  }, []);

  if (!isOpen || !account) return null;

  const transactions = account.transactions || { received: [], general: [], material: [] };
  
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';
  const fmtAmt = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(Number(amt) || 0);

  const tabs = [
    { id: 'received', label: 'Payment Received', icon: IndianRupee, data: transactions.received, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', amountKey: 'amountReceived', noteKey: 'remarks' },
    { id: 'material', label: 'Material Expenses', icon: ShoppingCart, data: transactions.material, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', amountKey: 'amount', noteKey: 'description' },
    { id: 'project', label: 'Project Expenses', icon: Briefcase, data: transactions.general, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', amountKey: 'amount', noteKey: 'description' },
  ];

  const activeTabData = tabs.find(t => t.id === activeTab);
  const totalAmount = activeTabData.data.reduce((sum, item) => sum + (Number(item[activeTabData.amountKey]) || 0), 0);

  const rawCompanyName = fetchedSettings?.companyName || companySettings?.name;
  const companyName = (!rawCompanyName || rawCompanyName.toLowerCase().includes("venus"))
    ? "Verve Kitchens"
    : rawCompanyName;

  const companyAddress = fetchedSettings?.companyAddress || companySettings?.address || "";
  const companyCity = fetchedSettings?.companyState || companySettings?.city || "";
  const companyCountry = "India";

  const rawLogoPath = fetchedSettings?.logoExpandedPath || companySettings?.logoUrl || "";
  const companyLogoUrl = formatLogoUrl(rawLogoPath);

  const handlePrint = () => {
    if (!printRef.current) return;
    const content = printRef.current.innerHTML;
    const win = window.open("", "_blank", "width=800,height=900");
    win.document.write(`
      <html>
        <head>
          <title>Account Statement - ${account.customerName}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Inter', 'Segoe UI', Arial, sans-serif; color: #1e293b; background: #fff; padding: 32px 36px; font-size: 12px; line-height: 1.6; -webkit-print-color-adjust: exact; print-color-adjust: exact; }

            .payslip-header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 16px; border-bottom: 2px solid #e2e8f0; margin-bottom: 20px; }
            .company-info { display: flex; align-items: center; gap: 14px; }
            .company-logo { width: 52px; height: 52px; border-radius: 10px; background: #f1f5f9; border: 1px solid #e2e8f0; padding: 4px; object-fit: contain; flex-shrink: 0; }
            .company-name { font-size: 18px; font-weight: 800; color: #0f172a; letter-spacing: -0.02em; }
            .company-addr { font-size: 10.5px; color: #64748b; margin-top: 2px; font-weight: 400; }

            .period-badge { text-align: right; }
            .period-label { font-size: 9px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.08em; }
            .period-value { font-size: 15px; font-weight: 800; color: #1e40af; margin-top: 3px; }

            .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 20px; }
            .info-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; }
            .info-card-title { font-size: 9px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 10px; }
            .info-row { display: flex; justify-content: space-between; align-items: baseline; padding: 4px 0; border-bottom: 1px solid #f1f5f9; }
            .info-row:last-child { border-bottom: none; }
            .info-label { font-size: 11px; color: #64748b; font-weight: 500; }
            .info-value { font-size: 11px; font-weight: 600; color: #1e293b; text-align: right; }

            .table-section-title { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 8px; padding-left: 2px; color: #334155; margin-top: 20px; }
            
            .comp-table { width: 100%; border-collapse: separate; border-spacing: 0; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; margin-bottom: 16px; }
            .comp-table th { padding: 8px 14px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid #e2e8f0; text-align: left; }
            .comp-table th.text-right { text-align: right; }
            .comp-table td { padding: 7px 14px; font-size: 11px; color: #334155; border-bottom: 1px solid #f1f5f9; }
            .comp-table tr:last-child td { border-bottom: none; }
            .comp-table .total-row { background: #f8fafc; font-weight: 700; }
            .comp-table .total-row td { border-top: 1px solid #e2e8f0; padding: 9px 14px; color: #0f172a; }

            .print-footer { margin-top: 32px; padding-top: 14px; border-top: 1px dashed #cbd5e1; text-align: center; font-size: 9.5px; color: #94a3b8; font-weight: 500; }

            @media print {
              body { padding: 20px; }
              .payslip-header { break-inside: avoid; }
              .info-grid { break-inside: avoid; }
              .comp-table { break-inside: avoid; }
            }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" title="Payment Transactions" hideHeader>
      <div className="flex flex-col h-[70vh] bg-slate-50 -mt-5 sm:-mt-6 rounded-t-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pb-6 pt-0 bg-white shrink-0">
          <div>
            <p className="text-sm text-slate-500 mt-4 flex items-center gap-3">
              <span className="font-semibold text-slate-700">{account.customerName}</span>
              <span className="w-1 h-1 rounded-full bg-slate-300"></span>
              <span>{account.customId}</span>
              <span className="w-1 h-1 rounded-full bg-slate-300"></span>
              <span className="font-medium text-slate-600">Budget: {fmtAmt(account.projectValue || account.budget)}</span>
            </p>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button onClick={handlePrint} className="btn-secondary flex items-center gap-1.5 text-[12px] bg-slate-50 hover:bg-slate-100 border border-slate-200 px-3 py-1.5 rounded-lg text-slate-700 font-medium transition-colors">
              <Printer className="w-3.5 h-3.5" /> Print / Download
            </button>
            {/* <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
              <X className="w-5 h-5" />
            </button> */}
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 bg-white border-b border-slate-200 shrink-0">
          <div className="flex gap-6">
            {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 pb-4 text-sm font-semibold transition-colors border-b-2 ${isActive ? 'text-brand-blue border-brand-blue' : 'text-slate-500 border-transparent hover:text-slate-700'}`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                  <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${isActive ? tab.bg + ' ' + tab.color : 'bg-slate-100 text-slate-500'}`}>
                    {tab.data.length}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          <div className="bg-white flex flex-col h-full">
            <div className="flex-1 overflow-auto">
              <table className="w-full text-left text-sm whitespace-nowrap">
                <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 font-semibold text-slate-600">Date</th>
                    <th className="px-6 py-4 font-semibold text-slate-600">Description / Note</th>
                    <th className="px-6 py-4 font-semibold text-slate-600 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {activeTabData.data.length > 0 ? (
                    activeTabData.data.map((item, idx) => (
                      <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4 text-slate-700 font-medium">
                          {fmtDate(item.paymentDate || item.expenseDate || item.date || item.createdAt)}
                        </td>
                        <td className="px-6 py-4 text-slate-600 max-w-[300px] truncate" title={item[activeTabData.noteKey] || '-'}>
                          {item[activeTabData.noteKey] || '-'}
                        </td>
                        <td className={`px-6 py-4 font-bold text-right ${activeTabData.color}`}>
                          {fmtAmt(item[activeTabData.amountKey])}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="px-6 py-12 text-center text-slate-500">
                        <div className="flex flex-col items-center justify-center">
                          <FileText className="w-12 h-12 text-slate-200 mb-3" />
                          <p>No transactions found for this category.</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Total Footer */}
            <div className={`shrink-0 px-6 py-4 border-t flex justify-between items-center ${activeTabData.bg} ${activeTabData.border}`}>
              <span className="font-bold text-slate-700 uppercase tracking-wider text-xs">Total {activeTabData.label}</span>
              <span className={`text-xl font-black ${activeTabData.color}`}>
                {fmtAmt(totalAmount)}
              </span>
            </div>
          </div>
        </div>

        {/* Hidden Print Container */}
        <div className="hidden">
          <div ref={printRef} className="bg-white p-8 max-w-3xl mx-auto">
            {/* ═══════ Header ═══════ */}
            <div className="payslip-header flex justify-between items-start pb-4 border-b-2 border-slate-200 mb-5">
              <div className="company-info flex items-center gap-3.5">
                {companyLogoUrl ? (
                  <img src={companyLogoUrl} alt="Logo" className="company-logo" />
                ) : (
                  <div className="company-logo" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: '900', color: '#1e40af', backgroundColor: '#eff6ff' }}>
                    {companyName.charAt(0)}
                  </div>
                )}
                <div>
                  <h1 className="company-name">{companyName}</h1>
                  <p className="company-addr">
                    {[companyAddress, companyCity, companyCountry].filter(Boolean).join(", ")}
                  </p>
                </div>
              </div>
              <div className="period-badge text-right">
                <p className="period-label">Account Statement</p>
                <p className="period-value">{new Date().toLocaleDateString("en-IN", { day: '2-digit', month: 'short', year: 'numeric' })}</p>
              </div>
            </div>

            {/* ═══════ Info Grid ═══════ */}
            <div className="info-grid">
              <div className="info-card">
                <p className="info-card-title">Customer Details</p>
                <div className="info-row">
                  <span className="info-label">Name</span>
                  <span className="info-value">{account.customerName}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Verve ID</span>
                  <span className="info-value">{account.customId}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Status</span>
                  <span className="info-value capitalize">{account.leadStatus || account.status || 'Active'}</span>
                </div>
              </div>

              <div className="info-card">
                <p className="info-card-title">Account Summary</p>
                <div className="info-row">
                  <span className="info-label">Project Value</span>
                  <span className="info-value">{fmtAmt(account.projectValue || account.budget)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Total Received</span>
                  <span className="info-value">{fmtAmt(account.totalReceived)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Balance Due</span>
                  <span className="info-value">{fmtAmt(account.balance)}</span>
                </div>
              </div>
            </div>

            {/* ═══════ Current Tab Table ═══════ */}
            <p className="table-section-title">{activeTabData.label} Transactions</p>
            <table className="comp-table">
              <thead>
                <tr>
                  <th style={{ backgroundColor: '#f8fafc', color: '#475569' }}>Date</th>
                  <th style={{ backgroundColor: '#f8fafc', color: '#475569' }}>Description / Note</th>
                  <th className="text-right" style={{ backgroundColor: '#f8fafc', color: '#475569' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {activeTabData.data.length > 0 ? (
                  activeTabData.data.map((item, idx) => (
                    <tr key={idx}>
                      <td>{fmtDate(item.paymentDate || item.expenseDate || item.date || item.createdAt)}</td>
                      <td>{item[activeTabData.noteKey] || '-'}</td>
                      <td className="text-right font-medium">{fmtAmt(item[activeTabData.amountKey])}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'center', color: '#94a3b8', padding: '20px' }}>
                      No transactions found for this category.
                    </td>
                  </tr>
                )}
                <tr className="total-row">
                  <td colSpan="2" style={{ textAlign: 'right' }}>Total {activeTabData.label}</td>
                  <td className="text-right" style={{ color: activeTabData.id === 'received' ? '#047857' : (activeTabData.id === 'material' ? '#b45309' : '#1d4ed8') }}>
                    {fmtAmt(totalAmount)}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* ═══════ Footer ═══════ */}
            <div className="print-footer">
              — This is a computer-generated document and does not require a physical signature. —
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
